import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import * as XLSX from "xlsx";
import CustomButton from "../pages/CustomButton";
import { Upload, Download } from "lucide-react";
import Loader from "../dist/Loader";
const SOBTargetCombined = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [quarterOptions, setQuarterOptions] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyEmployeeId, setHistoryEmployeeId] = useState(null);
  const [historyEmployeeName, setHistoryEmployeeName] = useState("");
  const [historyData, setHistoryData] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editRowData, setEditRowData] = useState({
    target: "",
    start_date: "",
  });

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [employees, setEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const printRef = useRef();

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        setStates(response.data);
      } catch (err) {
        console.error("Error fetching states:", err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    const fetchDivisions = async () => {
      if (selectedState) {
        try {
          const response = await axios.get(
            `${BASE_URL}/api/divisions?state_id=${selectedState}`,
            { headers: getAuthHeaders() }
          );
          setDivisions(response.data);
          setError(null);
        } catch (err) {
          console.error("Error fetching divisions:", err);
          setError("Failed to load divisions");
        }
      } else {
        setDivisions([]);
        setSelectedDivision("");
      }
    };
    fetchDivisions();
  }, [selectedState]);

  // Upload functions
  const normalizeDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return "";
    if (parts[0].length === 4) {
      return `${parts[2].padStart(2, "0")}-${parts[1].padStart(2, "0")}-${
        parts[0]
      }`;
    }
    return `${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}-${
      parts[2]
    }`;
  };

  const toYYYYMMDD = (ddmmyyyy) => {
    const [dd, mm, yyyy] = ddmmyyyy.split("-");
    return `${yyyy}-${mm}-${dd}`;
  };

  // --- NEW: quarter helpers (same fiscal mapping as SOB/POB) ---
  // returns label like "Q3-2025" for the given date using fiscal mapping:
  // Q1 Apr-Jun, Q2 Jul-Sep, Q3 Oct-Dec, Q4 Jan-Mar (label year is fiscal base year)
  const getFiscalQuarterLabelForDate = (date = new Date()) => {
    const month = date.getMonth(); // 0..11
    let q = "Q1";
    let fiscalYear = date.getFullYear();
    if (month >= 3 && month <= 5) {
      q = "Q1";
    } else if (month >= 6 && month <= 8) {
      q = "Q2";
    } else if (month >= 9 && month <= 11) {
      q = "Q3";
    } else {
      q = "Q4";
      fiscalYear = date.getFullYear() - 1; // Jan-Mar belong to previous fiscal label year
    }
    return `${q}-${fiscalYear}`;
  };

  // generate options for current fiscal year and next fiscal year, placing current quarter first
  const generateQuarterOptions = (forDate = new Date()) => {
    const month = forDate.getMonth();
    let fiscalBaseYear = forDate.getFullYear();
    if (month >= 0 && month <= 2) fiscalBaseYear = forDate.getFullYear() - 1;
    const fiscalYears = [fiscalBaseYear, fiscalBaseYear + 1];
    const quarters = [];
    fiscalYears.forEach((fy) => {
      ["Q1", "Q2", "Q3", "Q4"].forEach((q) => quarters.push(`${q}-${fy}`));
    });
    const current = getFiscalQuarterLabelForDate(forDate);
    return [current, ...quarters.filter((x) => x !== current)];
  };

  // helper to turn "Q3-2025" into "Q3 (Oct - Dec) 2025"
  const formatQuarterDisplay = (quarter) => {
    if (!quarter) return "";
    const [q, year] = quarter.split("-");
    const map = {
      Q1: "Apr - Jun",
      Q2: "Jul - Sep",
      Q3: "Oct - Dec",
      Q4: "Jan - Mar",
    };
    return `${q} (${map[q] || ""}) ${year}`;
  };

  useEffect(() => {
    const opts = generateQuarterOptions(new Date());
    setQuarterOptions(opts);
    if (!selectedQuarter) setSelectedQuarter(opts[0]);
  }, []);

  const handleFileUpload = async () => {
    setError("");
    setUploadStatus("");

    if (!selectedFile || !fromDate) {
      setError("Please select both a date and a file");
      return;
    }

    const normalizedInputDate = normalizeDate(fromDate);

    if (selectedFile.name.endsWith(".csv")) {
      try {
        const fileContent = await selectedFile.text();
        const rows = fileContent.split("\n").filter((row) => row.trim() !== "");

        if (rows.length < 2) {
          setError("CSV file must contain at least one data row.");
          return;
        }

        const headers = rows[0].split(",").map((h) => h.trim().toLowerCase());
        const dateColumnIndex = headers.findIndex(
          (header) => header.includes("from_date") || header.includes("date")
        );

        if (dateColumnIndex === -1) {
          setError('CSV file must contain a "from_date" column.');
          return;
        }

        const keyMap = {
          "emp id": "employee_id",
          empid: "employee_id",
          "employee name": "employee_name",
          name: "employee_name",
          division: "division",
          state: "state",
          hq: "hq",
          designation: "designation",
          phone: "phone",
          target: "target",
          from_date: "from_date",
          "from date": "from_date",
          date: "from_date",
        };

        const csvData = [];
        for (let i = 1; i < rows.length; i++) {
          const row = rows[i].split(",");
          const rawDate = row[dateColumnIndex]?.trim();
          const rowDate = normalizeDate(rawDate);

          if (!rowDate || rowDate !== normalizedInputDate) {
            setError(
              `Row ${
                i + 1
              } has from_date "${rawDate}" which doesn't match selected From Date: ${normalizedInputDate}`
            );
            return;
          }

          const rowData = {};
          headers.forEach((header, idx) => {
            const key =
              keyMap[header.trim().toLowerCase()] ||
              header.trim().toLowerCase();
            rowData[key] = row[idx]?.trim();
          });

          csvData.push({
            id: Date.now() + i,
            employee_id: rowData.employee_id || "",
            employee_name: rowData.employee_name || "",
            division: rowData.division || "",
            state: rowData.state || "",
            hq: rowData.hq || "",
            designation: rowData.designation || "",
            phone: rowData.phone || "",
            target: rowData.target || "",
            start_date: rowData.from_date || normalizedInputDate,
          });
        }

        setEmployees(csvData);
        setCurrentPage(1);
      } catch (err) {
        setError("Error reading CSV file. Please check the format.");
        return;
      }
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("from_date", toYYYYMMDD(normalizedInputDate));

    try {
      setUploadStatus("Uploading...");
      const response = await axios.post(
        `${BASE_URL}/api/sob-targets/upload`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setUploadStatus(response.data.message || "Upload successful!");
      setSelectedFile(null);
      document.getElementById("file-input").value = "";
      // Close the modal after successful upload
      setTimeout(() => {
        setUploadModalOpen(false);
        setEmployees([]);
      }, 1500);
    } catch (err) {
      let errorMsg = "Upload failed. Please try again.";
      const resData = err.response?.data;

      if (Array.isArray(resData)) {
        errorMsg = resData.map((e) => e.msg || JSON.stringify(e)).join(", ");
      } else if (typeof resData === "object") {
        errorMsg = resData.detail || resData.message || errorMsg;
      }

      setError(errorMsg);
      setUploadStatus("");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError("");
      setUploadStatus("");
    }
  };

  // Report functions
  const getQuarterDateRange = (quarter) => {
    const [q, year] = quarter.split("-");
    let startDate, endDate;
    switch (q) {
      case "Q1":
        startDate = `01/Apr/${year}`;
        endDate = `30/Jun/${year}`;
        break;
      case "Q2":
        startDate = `01/Jul/${year}`;
        endDate = `30/Sep/${year}`;
        break;
      case "Q3":
        startDate = `01/Oct/${year}`;
        endDate = `31/Dec/${year}`;
        break;
      case "Q4":
        startDate = `01/Jan/${parseInt(year) + 1}`;
        endDate = `31/Mar/${parseInt(year) + 1}`;
        break;
      default:
        startDate = `01/Apr/${year}`;
        endDate = `30/Jun/${year}`;
    }
    return `${startDate}-${endDate}`;
  };

  const generateReport = async () => {
    if (!selectedQuarter) {
      alert("Please select a quarter");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const division = divisions.find(
        (d) => String(d.id) === String(selectedDivision)
      );
      const divisionName = division?.name;

      const quarterDateRange = getQuarterDateRange(selectedQuarter);
      const params = {};
      if (selectedState) params.state_id = selectedState;
      if (divisionName) params.division = divisionName;
      if (quarterDateRange) params.quarter = quarterDateRange;

      const response = await axios.get(
        `${BASE_URL}/api/sob-targets/employee_target_report`,
        {
          params,
          headers: getAuthHeaders(),
        }
      );

      setReportData(response.data);
    } catch (err) {
      console.error("Error fetching report:", err);
      setError("Failed to generate report");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (value) => {
    return value
      ? parseFloat(value).toLocaleString("en-IN", {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        })
      : "0.00";
  };

  const handlePrint = () => {
    const printContents = printRef.current.innerHTML;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = printContents;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  const handleExportExcel = () => {
    if (!reportData.length) return;
    const tableData = reportData.map((item, index) => ({
      "S.No": index + 1,
      Empid: item.employee_id,
      Employee: item.employee_name,
      Division: item.division,
      State: item.state,
      HQ: item.hq,
      Designation: item.designation,
      DOJ: formatDate(item.final_joining_date),
      DOR: formatDate(item.resign_date),
      Phone: item.phone_no || "-",
      Target: item.target,
      "From Date": formatDate(item.start_date),
      "End Date": formatDate(item.end_date),
    }));
    const ws = XLSX.utils.json_to_sheet(tableData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "SOB_Report");
    XLSX.writeFile(wb, "SOB_Target_Report.xlsx");
  };

  const openHistoryModal = async (employee) => {
    try {
      setHistoryEmployeeId(employee.employee_id);
      setHistoryEmployeeName(employee.employee_name);
      const response = await axios.get(
        `${BASE_URL}/api/sob-targets/employee/${employee.employee_id}`,
        { headers: getAuthHeaders() }
      );
      setHistoryData(response.data);
      setHistoryModalOpen(true);
    } catch (err) {
      console.error("Error fetching SOB history:", err);
      alert("Failed to load SOB history");
    }
  };

  const closeHistoryModal = async () => {
    setHistoryModalOpen(false);
    setHistoryEmployeeId(null);
    setHistoryEmployeeName("");
    setHistoryData([]);
    if (selectedState && selectedDivision) {
      await generateReport();
    }
  };

  const handleEditRow = (row) => {
    setEditingRow(row.employee_id);
    setEditRowData({
      target: row.target || "",
      start_date: row.start_date ? row.start_date.split("T")[0] : "",
    });
  };

  const handleSaveRow = async (item) => {
    try {
      if (!editRowData.target || !editRowData.start_date) {
        alert("Please fill in target and start date.");
        return;
      }

      const payload = {
        target: parseFloat(editRowData.target),
        start_date: editRowData.start_date,
      };

      if (item.sob_id) {
        await axios.put(`${BASE_URL}/api/sob-targets/${item.sob_id}`, payload, {
          headers: getAuthHeaders(),
        });
        alert("Target updated successfully!");
      } else {
        await axios.post(
          `${BASE_URL}/api/sob-targets/`,
          { ...payload, employee_id: item.employee_id, is_active: true },
          { headers: getAuthHeaders() }
        );
        alert("New target added successfully!");
      }

      await generateReport();
      setEditingRow(null);
    } catch (err) {
      console.error("Error saving target:", err);
      alert(err.response?.data?.detail || "Failed to save target");
    }
  };

  const handleDeleteRow = async (item) => {
    if (!item.sob_id) {
      alert("No target to delete for this employee.");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this target?")) return;

    try {
      await axios.delete(`${BASE_URL}/api/sob-targets/${item.sob_id}`, {
        headers: getAuthHeaders(),
      });
      await generateReport();
      alert("Target deleted successfully!");
    } catch (err) {
      console.error("Error deleting target:", err);
      alert("Failed to delete target");
    }
  };

  const handleEditRowChange = (e) => {
    const { name, value } = e.target;
    setEditRowData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Upload pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = employees.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(employees.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const openUploadModal = () => {
    setUploadModalOpen(true);
    setFromDate("");
    setSelectedFile(null);
    setUploadStatus("");
    setEmployees([]);
    setError("");
  };

  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setFromDate("");
    setSelectedFile(null);
    setUploadStatus("");
    setEmployees([]);
    setError("");
  };

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      <main className="flex-1">
        {/* Report Section */}
        <div className="p-2 border-2 border-gray-300 rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <div className="flex justify-center w-full">
              <h1
                className="text-xl sm:text-2xl font-bold text-center 
                      bg-gradient-to-r from-blue-700 via-blue-500 to-blue-300 
                      bg-clip-text text-transparent"
              >
                SOB Target Report
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {/* Upload Button */}
              <button
                onClick={openUploadModal}
                className="flex items-center gap-2 bg-blue-600 text-white font-medium px-4 py-2 rounded-lg hover:bg-blue-700 active:scale-[0.98] transition-all duration-200 text-nowrap"
              >
                <Upload className="w-5 h-5" />
                <span>Upload Targets</span>
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-5 items-center justify-center">
            <div>
              <label>State</label>
              <br />
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-60 p-1.5 text-sm border border-gray-300"
              >
                <option value="">All</option>{" "}
                {/* empty value means All => won't be sent to API */}
                {states.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label>Division</label>
              <br />
              <select
                value={selectedDivision}
                onChange={(e) => setSelectedDivision(e.target.value)}
                className="w-60 p-1.5 text-sm border border-gray-300"
              >
                <option value="">All</option> {/* empty value => All */}
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label>Quarter</label>
              <br />
              <select
                value={selectedQuarter}
                onChange={(e) => setSelectedQuarter(e.target.value)}
                className="w-60 p-1.5 text-sm border border-gray-300"
              >
                {quarterOptions.map((q) => (
                  <option key={q} value={q}>
                    {formatQuarterDisplay(q)}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-5">
              {isLoading ? (
                <div className="flex justify-center items-center">
                  <Loader />
                </div>
              ) : (
                <CustomButton
                  onClick={generateReport}
                  label="Go"
                  variant="go"
                />
              )}
            </div>
          </div>
        </div>

        {error && <div className="text-red-600 font-medium mb-4">{error}</div>}

        {/* Report Data Table */}
        {reportData.length > 0 && (
          <div className="p-4 mt-5 border-2 border-gray-300 rounded-lg shadow-sm">
            <div ref={printRef}>
              <div className="border rounded shadow  table-wrapper">
                <table className="unlock-dcr-leave-table text-sm ">
                  <thead className="bg-blue-500 text-white">
                    <tr>
                      {[
                        "S.No",
                        "Empid",
                        "Employee Name",
                        "Division",
                        "State",
                        "HQ",
                        "Designation",
                        "DOJ",
                        "DOR",
                        "Phone",
                        "Target",
                        "From Date",
                        "End Date",
                        "Actions",
                        "History",
                      ].map((h, i) => (
                        <th key={i} className="border border-black p-1.5">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((item, i) => (
                      <tr key={item.employee_id}>
                        <td className="border border-black p-1.5">{i + 1}</td>
                        <td className="border border-black p-1.5">
                          {item.employee_id}
                        </td>
                        <td className="border border-black p-1.5">
                          {item.employee_name}
                        </td>
                        <td className="border border-black p-1.5">
                          {item.division}
                        </td>
                        <td className="border border-black p-1.5">
                          {item.state}
                        </td>
                        <td className="border border-black p-1.5">{item.hq}</td>
                        <td className="border border-black p-1.5">
                          {item.designation}
                        </td>
                        <td className="border border-black p-1.5">
                          {formatDate(item.final_joining_date)}
                        </td>
                        <td className="border border-black p-1.5">
                          {formatDate(item.resign_date)}
                        </td>
                        <td className="border border-black p-1.5">
                          {item.phone_no || "-"}
                        </td>
                        <td className="border border-black p-1.5 text-right">
                          {editingRow === item.employee_id ? (
                            <input
                              type="number"
                              name="target"
                              value={editRowData.target}
                              onChange={handleEditRowChange}
                              className="w-full p-1 border"
                              step="0.01"
                              min="0"
                            />
                          ) : (
                            formatCurrency(item.target)
                          )}
                        </td>
                        <td className="border border-black p-1.5">
                          {editingRow === item.employee_id ? (
                            <input
                              type="date"
                              name="start_date"
                              value={editRowData.start_date}
                              onChange={handleEditRowChange}
                              className="w-full p-1 border"
                            />
                          ) : (
                            formatDate(item.start_date)
                          )}
                        </td>
                        <td className="border border-black p-1.5">
                          {formatDate(item.end_date)}
                        </td>
                        <td className="border border-black p-1.5 text-center">
                          {editingRow === item.employee_id ? (
                            <>
                              <CustomButton
                                onClick={() => handleSaveRow(item)}
                                label={item.sob_id ? "Save" : "Add"}
                                variant={item.sob_id ? "go" : "add"}
                              />
                              <CustomButton
                                onClick={() => setEditingRow(null)}
                                label="Cancel"
                                variant="cancel"
                              />
                            </>
                          ) : (
                            <>
                              {item.sob_id ? (
                                <>
                                  <CustomButton
                                    onClick={() => handleEditRow(item)}
                                    label="Edit"
                                    variant="edit"
                                  />
                                  <CustomButton
                                    onClick={() => handleDeleteRow(item)}
                                    label="Delete"
                                    variant="delete"
                                  />
                                </>
                              ) : (
                                <CustomButton
                                  onClick={() => handleEditRow(item)}
                                  label="Add Target"
                                  variant="add"
                                />
                              )}
                            </>
                          )}
                        </td>
                        <td className="border border-black p-1.5 text-center">
                          <CustomButton
                            onClick={() => openHistoryModal(item)}
                            label="History"
                            variant="history"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="sob-button-container mt-4 flex gap-4 justify-center">
                  <CustomButton
                    onClick={handlePrint}
                    label="Print"
                    variant="print"
                  >
                    🖨
                  </CustomButton>
                  <CustomButton
                    onClick={handleExportExcel}
                    label="Export"
                    variant="export"
                  >
                    📥
                  </CustomButton>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Upload Modal */}
      {uploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-4xl p-4 overflow-y-auto max-h-screen">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Upload SOB Targets</h2>
              <button
                onClick={closeUploadModal}
                className="text-gray-600 hover:text-gray-800 text-lg font-bold"
              >
                &times;
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From Date
                  </label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File (CSV/Excel)
                  </label>
                  <input
                    id="file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="w-full p-2 border border-gray-300 rounded"
                    required
                  />
                </div>
                <div className="pt-2">
                  <CustomButton
                    onClick={handleFileUpload}
                    label="Upload"
                    variant="go"
                    fullWidth
                  />
                </div>

                {uploadStatus && (
                  <div
                    className={`mt-2 p-2 rounded text-center ${
                      uploadStatus.includes("success")
                        ? "bg-green-100 text-green-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {uploadStatus}
                  </div>
                )}
                {error && (
                  <div className="mt-2 p-2 rounded bg-red-100 text-red-700 text-center">
                    {error}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-lg font-medium mb-2">File Requirements</h3>
                <ul className="list-disc pl-5 space-y-1 text-sm text-gray-600">
                  <li>CSV or Excel file format</li>
                  <li>
                    Must contain columns: Employee ID, Employee Name, Target
                  </li>
                  <li>
                    Optional columns: Division, State, HQ, Designation, Phone
                  </li>
                  <li>Date format: DD-MM-YYYY or YYYY-MM-DD</li>
                  <li>Target values should be numeric</li>
                </ul>
              </div>
            </div>

            {/* Preview Table */}
            {employees.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-medium mb-2">
                  Preview ({employees.length} records)
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-300 border-collapse text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border border-gray-300 p-1.5">S.No</th>
                        <th className="border border-gray-300 p-1.5">Empid</th>
                        <th className="border border-gray-300 p-1.5">
                          Employee
                        </th>
                        <th className="border border-gray-300 p-1.5">Target</th>
                        <th className="border border-gray-300 p-1.5">
                          From Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((employee, index) => (
                        <tr key={employee.id || index}>
                          <td className="border border-gray-300 p-1.5 text-center">
                            {indexOfFirstItem + index + 1}
                          </td>
                          <td className="border border-gray-300 p-1.5">
                            {employee.employee_id}
                          </td>
                          <td className="border border-gray-300 p-1.5">
                            {employee.employee_name}
                          </td>
                          <td className="border border-gray-300 p-1.5 text-right">
                            {employee.target}
                          </td>
                          <td className="border border-gray-300 p-1.5">
                            {employee.start_date
                              ? normalizeDate(employee.start_date)
                              : ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  {employees.length > 0 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-gray-100 border-t border-gray-300 mt-4">
                      <div className="flex items-center mb-2 sm:mb-0">
                        <label
                          htmlFor="itemsPerPage"
                          className="mr-2 font-medium text-sm text-gray-700"
                        >
                          View
                        </label>
                        <select
                          id="itemsPerPage"
                          value={itemsPerPage}
                          onChange={(e) => {
                            setCurrentPage(1);
                            setItemsPerPage(parseInt(e.target.value));
                          }}
                          className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
                        >
                          {[10, 20, 30, 50].map((count) => (
                            <option key={count} value={count}>
                              {count}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className="text-sm text-gray-600 font-medium">
                          {indexOfFirstItem + 1}-
                          {Math.min(indexOfLastItem, employees.length)} of{" "}
                          {employees.length}
                        </span>
                        <button
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          &larr;
                        </button>
                        <button
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          &rarr;
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-3xl p-4 overflow-y-auto max-h-screen">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                SOB Target History - {historyEmployeeName}
              </h2>
              <button
                onClick={closeHistoryModal}
                className="text-gray-600 hover:text-gray-800 text-lg font-bold"
              >
                &times;
              </button>
            </div>
             <table className="unlock-dcr-leave-table text-sm ">
          <thead className="bg-blue-500 text-white">

                <tr>
                  <th className="border border-black p-1.5">S.No</th>
                  <th className="border border-black p-1.5">SOB Target</th>
                  <th className="border border-black p-1.5">Start</th>
                  <th className="border border-black p-1.5">End</th>
                </tr>
              </thead>
              <tbody>
                {historyData.map((row, index) => (
                  <tr key={row.id}>
                    <td className="border border-black p-1.5 text-center">
                      {index + 1}
                    </td>
                    <td className="border border-black p-1.5">
                      {parseFloat(row.target).toFixed(2)}
                    </td>
                    <td className="border border-black p-1.5">
                      {new Date(row.start_date).toLocaleDateString()}
                    </td>
                    <td className="border border-black p-1.5">
                      {row.end_date
                        ? new Date(row.end_date).toLocaleDateString()
                        : "Till Next Change"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SOBTargetCombined;
